

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Icon
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function InvoiceViewer({
    open,
    onClose,
    invoice,
    invoiceItems,
    onSendEmail,
    isEmailLoading,
    formatCurrencyByAccount,
    colors
}) {
    const calculateTotalAmount = (items = []) => {
        let subtotal = 0;
        let taxSummary = {};

        items.forEach((item) => {
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discountPercentage = parseFloat(item.discount_percentage) || 0;
            const taxRate = parseFloat(item.tax) || 0;

            const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
            const totalDiscountedPrice = discountedPrice * quantity;

            subtotal += totalDiscountedPrice;

            // Tax on discounted net (after discount).
            const taxAmount = totalDiscountedPrice * (taxRate / 100);
            if (taxRate) {
                taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
            }
            item.discounted_price = discountedPrice;
            item.total_discounted_price = totalDiscountedPrice;
            item.tax_amount = taxAmount;
            item.total_price = totalDiscountedPrice + taxAmount;
        });

        const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);
        const grandTotal = subtotal + totalTax;

        return { subtotal, grandTotal, taxSummary };
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    width: '1000px',
                    maxWidth: '100%'
                }
            }}
        >
            <Box className="pearl-popup-header"
                sx={{
                    backgroundColor: colors.popupHeader.background,
                    px: 3,
                    py: 2.5,
                }}
            >
                <MDTypography
                    variant="h6"
                    component="h2"
                    sx={{ color: '#FFFFFF' }}
                >
                    View Invoice
                </MDTypography>
            </Box>

            <DialogContent sx={{ p: 3, mt: 1 }}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        { label: "Invoice Number", value: invoice?.invoice_number },
                        { label: "Status", value: invoice?.status },
                        { label: "Account", value: invoice?.account_name },
                        { label: "Invoice Date", value: invoice?.invoice_date },
                        { label: "Due Date", value: invoice?.due_date }
                    ].map((field, index) => (
                        <Grid item xs={3} key={index}>
                            <Box sx={{
                                backgroundColor: 'grey.100',
                                p: 1.5,
                                borderRadius: 2,
                                height: '100%'
                            }}>
                                <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        mb: 0.5
                                    }}
                                >
                                    {field.label}
                                </MDTypography>
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {field.value || '-'}
                                </MDTypography>
                            </Box>
                        </Grid>
                    ))}
                    {invoice?.notes && (
                        <Grid item xs={12}>
                            <Box sx={{
                                backgroundColor: 'grey.100',
                                p: 1.5,
                                borderRadius: 1,
                                mt: 1
                            }}>
                                <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        fontSize: '0.75rem',
                                        mb: 0.5
                                    }}
                                >
                                    Notes
                                </MDTypography>
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 400,
                                        lineHeight: 1.5
                                    }}
                                >
                                    {invoice.notes}
                                </MDTypography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                <MDBox
                    sx={{
                        mt: 3,
                        mb: 3,
                        border: "1px solid #dee2e6",
                        borderRadius: "0.75rem",
                        overflow: "auto",
                        maxHeight: "400px"
                    }}
                >
                    {/* Header */}
                    <MDBox
                        sx={{
                            display: "flex",
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #dee2e6",
                            position: "sticky",
                            top: 0,
                            zIndex: 1
                        }}
                    >
                        <MDBox width="25%" p={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Product Name
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Unit Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="8%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Tax %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discount %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discounted Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="10%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Quantity
                            </MDTypography>
                        </MDBox>
                        <MDBox width="21%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Total Net Price
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    {/* Body */}
                    <MDBox>
                        {invoiceItems.map((item) => {
                            const discountedPrice = (item.unit_price || 0) - ((item.unit_price || 0) * (item.discount_percentage || 0) / 100);
                            const totalNetPrice = discountedPrice * (item.quantity || 0);
                            return (
                                <MDBox
                                    key={item.id}
                                    display="flex"
                                    sx={{
                                        borderBottom: "1px solid #dee2e6",
                                        '&:hover': { backgroundColor: '#f8f9fa' }
                                    }}
                                >
                                    <MDBox width="25%" p={2}>
                                        <MDTypography variant="button" fontWeight="regular">
                                            {item.product_name}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {formatCurrencyByAccount(item.unit_price || 0, invoice?.currency || invoice?.account_currency)}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="8%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {item.tax}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {item.discount_percentage}%
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {formatCurrencyByAccount(discountedPrice, invoice?.currency || invoice?.account_currency)}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="10%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {item.quantity}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="21%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {formatCurrencyByAccount(totalNetPrice, invoice?.currency || invoice?.account_currency)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        })}
                    </MDBox>
                </MDBox>

                <MDBox 
                    sx={{ 
                        mt: 3, 
                        p: 2, 
                        borderTop: '2px solid #dee2e6',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >
                    <MDBox sx={{ minWidth: '300px' }}>
                        {(() => {
                            const { subtotal, grandTotal, taxSummary } = calculateTotalAmount(invoiceItems);
                            const currency = invoice?.currency || invoice?.account_currency || 'INR';

                            return (
                                <MDBox>
                                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                                        <MDTypography variant="button" color="text" fontWeight="regular">
                                            Subtotal:
                                        </MDTypography>
                                        <MDTypography variant="button" color="text" fontWeight="medium">
                                            {formatCurrencyByAccount(subtotal, currency)}
                                        </MDTypography>
                                    </MDBox>

                                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                                        <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                                            <MDTypography variant="button" color="text" fontWeight="regular">
                                                Tax ({taxRate}%):
                                            </MDTypography>
                                            <MDTypography variant="button" color="text" fontWeight="medium">
                                                {formatCurrencyByAccount(taxAmount, currency)}
                                            </MDTypography>
                                        </MDBox>
                                    ))}

                                    <MDBox 
                                        display="flex" 
                                        justifyContent="space-between" 
                                        mt={2}
                                        pt={1.5}
                                        sx={{ borderTop: '1px solid #dee2e6' }}
                                    >
                                        <MDTypography variant="button" color="dark" fontWeight="medium">
                                            Total Amount:
                                        </MDTypography>
                                        <MDTypography variant="button" color="info" fontWeight="bold">
                                            {formatCurrencyByAccount(grandTotal, currency)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        })()}
                    </MDBox>
                </MDBox>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <MDButton
                    onClick={onSendEmail}
                    variant="outlined"
                    color="info"
                    startIcon={<Icon>email</Icon>}
                    disabled={isEmailLoading}
                    sx={{ mr: 1 }}
                >
                    {isEmailLoading ? 'Preparing Email...' : 'Send Email'}
                </MDButton>
                <MDButton
                    onClick={onClose}
                    variant="contained"
                    color="info"
                >
                    Close
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default InvoiceViewer;